import { Grid, makeStyles, Typography } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import CategoryIcon from '@material-ui/icons/Category';
import LibraryBooksIcon from '@material-ui/icons/LibraryBooks';
import CodeIcon from '@material-ui/icons/Code';

import { Content, Page, Header, Link } from '@backstage/core-components';
import {
  HomePageCompanyLogo,
  HomePageStarredEntities,
  HomePageTopVisited,
  HomePageRecentlyVisited,
} from '@backstage/plugin-home';
import { HomePageSearchBar } from '@backstage/plugin-search';
import { SearchContextProvider } from '@backstage/plugin-search-react';
import { identityApiRef, useApi } from '@backstage/core-plugin-api';
import { useEffect, useState } from 'react';

import { LogoFullDark } from './LogoFullDark';

const useStyles = makeStyles(theme => ({
  searchBarInput: {
    maxWidth: 560,
    width: '100%',
    margin: 'auto',
    backgroundColor: theme.palette.background.paper,
    borderRadius: '50px',
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: 'none',
    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
    '&:focus-within': {
      borderColor: theme.palette.secondary.main,
      boxShadow: '0 0 0 3px rgba(250, 70, 22, 0.06)',
    },
  },
  searchBarOutline: {
    borderStyle: 'none',
  },
  heroSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    paddingTop: theme.spacing(6),
    paddingBottom: theme.spacing(0.5),
  },
  greeting: {
    fontFamily: "'Lexend Peta', 'Poppins', sans-serif",
    fontWeight: 600,
    fontSize: '1.5rem',
    color: theme.palette.text.primary,
    marginTop: theme.spacing(2),
    letterSpacing: '-0.01em',
  },
  dateText: {
    fontFamily: "'Inter', sans-serif",
    color: theme.palette.text.secondary,
    fontWeight: 400,
    fontSize: '0.8125rem',
    marginTop: theme.spacing(0.5),
    letterSpacing: '0.02em',
  },
  searchContainer: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    marginBottom: theme.spacing(4),
  },
  quickActions: {
    display: 'flex',
    justifyContent: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(5),
    flexWrap: 'wrap',
  },
  quickActionCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: theme.spacing(2.5, 3.5),
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    cursor: 'pointer',
    transition: 'all 0.25s ease',
    backgroundColor: theme.palette.background.paper,
    textDecoration: 'none',
    color: theme.palette.text.primary,
    minWidth: 130,
    '&:hover': {
      borderColor: theme.palette.secondary.main,
      boxShadow: '0 8px 24px rgba(250,70,22,0.1)',
      transform: 'translateY(-3px)',
      '& $quickActionIcon': {
        transform: 'scale(1.1)',
      },
    },
  },
  quickActionIcon: {
    color: theme.palette.secondary.main,
    marginBottom: theme.spacing(1),
    fontSize: 28,
    transition: 'transform 0.25s ease',
  },
  quickActionLabel: {
    fontFamily: "'Poppins', sans-serif",
    fontSize: '0.8125rem',
    fontWeight: 500,
  },
  searchRow: {
    marginTop: theme.spacing(4),
  },
  cardsContainer: {
    margin: theme.spacing(0, 0, 3, 0),
  },
}));

const formatDate = (): string => {
  const now = new Date();
  const options: Intl.DateTimeFormatOptions = {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  };
  const formatted = now.toLocaleDateString('pt-BR', options);
  return formatted.charAt(0).toUpperCase() + formatted.slice(1);
};

const quickActions = [
  { label: 'Novo Componente', icon: AddIcon, path: '/create' },
  { label: 'Catálogo', icon: CategoryIcon, path: '/catalog' },
  { label: 'Documentação', icon: LibraryBooksIcon, path: '/docs' },
  { label: 'Spassu Code', icon: CodeIcon, path: '/spassu-code' },
];

const SpassuHomeLogo = () => {
  const classes = useStyles();
  return (
    <div className={classes.heroSection}>
      <LogoFullDark />
    </div>
  );
};

export const HomePage = () => {
  const classes = useStyles();
  const identityApi = useApi(identityApiRef);
  const [displayName, setDisplayName] = useState('');

  useEffect(() => {
    let cancelled = false;
    identityApi.getProfileInfo().then(profile => {
      if (!cancelled) {
        setDisplayName(profile.displayName ?? '');
      }
    });
    return () => {
      cancelled = true;
    };
  }, [identityApi]);

  return (
    <SearchContextProvider>
      <Page themeId="home">
        <Header title="Spassu Code" subtitle="Plataforma de Desenvolvimento" />
        <Content>
          <Grid container justifyContent="center" spacing={0}>
            <Grid item xs={12}>
              <HomePageCompanyLogo logo={<SpassuHomeLogo />} />
              <Typography align="center" className={classes.greeting}>
                {displayName ? `Olá, ${displayName}!` : 'Bem-vindo(a)!'}
              </Typography>
              <Typography align="center" className={classes.dateText}>
                {formatDate()}
              </Typography>
            </Grid>
          </Grid>

          <Grid
            container
            justifyContent="center"
            spacing={0}
            className={classes.searchRow}
          >
            <Grid item xs={12} md={8}>
              <div className={classes.searchContainer}>
                <HomePageSearchBar
                  InputProps={{
                    classes: {
                      root: classes.searchBarInput,
                      notchedOutline: classes.searchBarOutline,
                    },
                  }}
                  placeholder="Buscar componentes, APIs, documentos..."
                />
              </div>
            </Grid>
          </Grid>

          <div className={classes.quickActions}>
            {quickActions.map(action => (
              <Link
                key={action.path}
                to={action.path}
                underline="none"
                className={classes.quickActionCard}
              >
                <action.icon className={classes.quickActionIcon} />
                <Typography className={classes.quickActionLabel}>
                  {action.label}
                </Typography>
              </Link>
            ))}
          </div>

          <Grid container spacing={3} className={classes.cardsContainer}>
            <Grid item xs={12} md={6}>
              <HomePageTopVisited />
            </Grid>
            <Grid item xs={12} md={6}>
              <HomePageRecentlyVisited />
            </Grid>
          </Grid>

          <Grid container spacing={3} className={classes.cardsContainer}>
            <Grid item xs={12}>
              <HomePageStarredEntities />
            </Grid>
          </Grid>
        </Content>
      </Page>
    </SearchContextProvider>
  );
};
